import { useEffect, useState } from 'react';
import { Box, CircularProgress, Container, Fade, Grid, Paper, useTheme } from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { clearTripDetails, fetchTripDetails } from '@acorn-portal/shared/src/store/telematicsTripDetailsSlice';
import {
	TripEventsList,
	TripFullMap,
	TripNotFoundPanel,
	TripScoreBreakdown,
	TripSummaryHeader,
} from '@acorn-portal/shared/src/components/telematics/trip';
import { TelematicsErrorPanel } from '@acorn-portal/shared/src/components/telematics/common';
import { useDriverScoreVisibility } from '@acorn-portal/shared/src/hooks/useDriverScoreVisibility';

/**
 * Trip details page: summary header, full route map, events list and score breakdown.
 *
 * The page is "pending" until a fetch has been dispatched for the current trip id
 * and has finished, so the not-found state can never flash before the first fetch.
 */
const TripDetailsPage: React.FC = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { tripId } = useParams<{ tripId: string }>();
	const policyNumber = useAppSelector((state) => state.fullPolicy.policyNumber);
	const { tripDetails, loading, error } = useAppSelector((state) => state.telematicsTripDetails);
	const showDriverScore = useDriverScoreVisibility();

	/** The trip id a fetch has been dispatched for. Until it matches `tripId`, the page is pending. */
	const [requestedTripId, setRequestedTripId] = useState<string | null>(null);
	const [highlightedEventId, setHighlightedEventId] = useState<string | undefined>(undefined);

	// Fetch once per trip id, as soon as the policy number is known
	useEffect(() => {
		if (!tripId || !policyNumber || requestedTripId === tripId) return;
		setRequestedTripId(tripId);
		dispatch(clearTripDetails());
		dispatch(fetchTripDetails({ tripId, policyReference: policyNumber }));
	}, [dispatch, tripId, policyNumber, requestedTripId]);

	// Clear stale details when leaving the page
	useEffect(() => {
		return () => {
			dispatch(clearTripDetails());
		};
	}, [dispatch]);

	const isPending = loading || requestedTripId !== tripId;
	const hasError = !isPending && Boolean(error);
	const isNotFound = !isPending && !error && !tripDetails;
	const hasTrip = !isPending && !error && Boolean(tripDetails);

	const handleBack = () => navigate(-1);

	const handleRetry = () => {
		if (!tripId || !policyNumber) return;
		dispatch(fetchTripDetails({ tripId, policyReference: policyNumber }));
	};

	/** Selecting an event on the map or in the list highlights it in both. */
	const handleEventSelect = (eventId: string) => setHighlightedEventId(eventId);

	return (
		<Fade in timeout={400}>
			<Paper
				elevation={1}
				sx={{
					margin: { xs: theme.spacing(1), sm: theme.spacing(1), md: theme.spacing(3) },
					borderRadius: '4px',
					backgroundColor: theme.palette.background.paper,
					overflow: 'hidden',
				}}
			>
				<Container maxWidth='md'>
					{isPending && (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
							<CircularProgress />
						</Box>
					)}

					{hasError && <TelematicsErrorPanel onRetry={handleRetry} onBack={handleBack} />}

					{isNotFound && <TripNotFoundPanel onBack={handleBack} />}
				</Container>

				{hasTrip && tripDetails && (
					<Container maxWidth='md'>
						<Box
							sx={{
								p: { xs: 2, sm: 2, md: 3 },
								borderBottom: `1px solid ${theme.palette.divider}`,
							}}
						>
							<TripSummaryHeader
								startTimeUtc={tripDetails.startTimeUtc}
								durationSeconds={tripDetails.durationSeconds}
								distanceMiles={tripDetails.distanceMiles}
								startAddress={tripDetails.startAddress}
								endAddress={tripDetails.endAddress}
								totalScore={showDriverScore ? tripDetails.riskScores?.totalScore : undefined}
								onBack={handleBack}
							/>
						</Box>

						<TripFullMap
							polyline={tripDetails.polyline}
							events={tripDetails.events}
							onEventClick={handleEventSelect}
							height={500}
						/>

						<Grid container spacing={3} sx={{ mt: 3, pb: 3 }}>
							<Grid size={{ xs: 12, md: 6 }}>
								<TripEventsList
									events={tripDetails.events}
									highlightedEventId={highlightedEventId}
									onEventHighlight={handleEventSelect}
								/>
							</Grid>

							<Grid size={{ xs: 12, md: 6 }}>
								<TripScoreBreakdown riskScores={tripDetails.riskScores} showScores={showDriverScore} />
							</Grid>
						</Grid>
					</Container>
				)}
			</Paper>
		</Fade>
	);
};

export default TripDetailsPage;
